<script setup>
  import { stringify } from 'qs'

  const { locale, t } = useI18n()
  const config = useRuntimeConfig()
  const { generateSeoMeta } = useSeo()

  const pagesStore = usePagesStore()
  const useBreadcrumbs = useBreadcrumbsStore()

  const tabPopulate = {
    on: {
      'shared.tab-title': true,
      'shared.ck-editor': true,
      'shared.rich-text': true,
      'shared.youtube': true,
      'shared.media': {
        populate: {
          file: {
            fields: ['url']
          }
        }
      },
      'shared.cta': {
        populate: {
          file: {
            fields: ['url']
          }
        }
      }
    }
  }

  const { data: page, pending: pageLoading } = await useAsyncData(
    `page-refer-a-friend-${locale.value}`,
    async () => {
      try {
        await pagesStore.fetchOneBySlug(
          t('slugs.sponsorColleague'),
          locale.value,
          stringify({
            populate: {
              banner: true,
              seo: {
                populate: ['shareImage']
              },
              tab1: tabPopulate,
              tab2: tabPopulate,
              tab3: tabPopulate,
              tab4: tabPopulate,
              tab5: tabPopulate,
              tab6: tabPopulate
            }
          })
        )
        return pagesStore.item || {}
      } catch (error) {
        console.error('Error fetching page:', error)
        return {}
      }
    },
    {
      watch: [locale]
    }
  )

  const activeTab = ref('tab1')

  const tabs = computed(() => {
    if (!page.value) return []
    return ['tab1', 'tab2', 'tab3', 'tab4', 'tab5', 'tab6'].filter((key) => page.value[key]?.length)
  })

  function getTabLabel(tabKey) {
    const tabData = page.value?.[tabKey] || []
    const titleComponent = tabData.find((item) => item.__component === 'shared.tab-title')
    return titleComponent?.Title || ''
  }

  /**
   * Groups the active tab's blocks in order, merging consecutive CTAs
   * so they render side by side via the CommonCta component.
   */
  const groupedBlocks = computed(() => {
    const items = page.value?.[activeTab.value]?.filter((b) => b.__component !== 'shared.tab-title') || []

    const groups = []
    let currentCtaGroup = null

    for (const item of items) {
      if (item.__component === 'shared.cta') {
        if (!currentCtaGroup) {
          currentCtaGroup = { type: 'cta-group', ctas: [] }
          groups.push(currentCtaGroup)
        }
        currentCtaGroup.ctas.push(item)
      } else {
        currentCtaGroup = null
        groups.push({ type: 'block', item })
      }
    }

    return groups
  })

  watchEffect(() => {
    if (page.value) {
      useBreadcrumbs.set([
        {
          name: t('menu.mainMenu.members'),
          path: 'membership'
        },
        {
          name: page.value?.title || t('pages.sponsorColleague.title'),
          path: 'membership-refer-a-friend'
        }
      ])
    }
  })

  useHead(() =>
    generateSeoMeta({
      title: page.value?.seo?.metaTitle || page.value?.title,
      description: page.value?.seo?.metaDescription,
      image: page.value?.seo?.shareImage || page.value?.banner,
      basePath: `/${t('slugs.sponsorColleague')}`
    })
  )
</script>

<template>
  <div>
    <CommonLoading v-if="pageLoading" />
    <template v-else>
      <!-- Hero Banner -->
      <div
        class="relative overflow-hidden h-80 lg:h-96 flex items-end justify-center"
        :style="
          page?.banner
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 40%, rgba(0, 0, 0, 0.7) 100%), url(${config.public.strapi}${page.banner.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }
            : {
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
              }
        "
      >
        <!-- Animated background elements -->
        <div class="absolute inset-0 opacity-20">
          <div
            class="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"
          ></div>
          <div
            class="absolute top-1/3 right-1/3 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"
          ></div>
        </div>

        <div class="container relative z-10 pb-12">
          <div class="max-w-4xl mx-auto text-center">
            <!-- Badge -->
            <div
              class="inline-flex items-center gap-2 rounded-full bg-sky-500/20 backdrop-blur-sm border border-sky-400/40 px-4 py-2 mb-6 animate-fade-in"
            >
              <div class="w-2 h-2 bg-sky-400 rounded-full animate-pulse"></div>
              <span class="text-sm font-semibold text-sky-300 uppercase tracking-wider">
                {{ t('pages.sponsorColleague.badge') }}
              </span>
            </div>

            <!-- Title -->
            <h1
              class="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight animate-fade-in-up animation-delay-200"
            >
              {{ page?.title }}
            </h1>

            <!-- CTA Button (hardcoded) -->
            <div class="animate-fade-in-up animation-delay-600">
              <a
                :href="t('pages.sponsorColleague.ctaUrl')"
                target="_blank"
                rel="noopener noreferrer"
                class="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                <font-awesome :icon="['fas', 'user-plus']" class="mr-3 relative z-10" />
                <span class="relative z-10">
                  {{ t('pages.sponsorColleague.ctaLabel') }}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="container mt-[72px] mb-4 lg:mb-8 grid grid-cols-1 lg:grid-cols-8 gap-4">
        <div class="px-8" :class="tabs.length > 1 ? 'lg:col-span-6' : 'lg:col-span-8'">
          <!-- Tab switcher (only shown once the page has more than one populated tab) -->
          <div v-if="tabs.length > 1" class="flex flex-wrap gap-3 mb-8">
            <button
              v-for="tab in tabs"
              :key="tab"
              type="button"
              class="px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200"
              :class="
                activeTab === tab
                  ? 'bg-sky-800 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-sky-300'
              "
              @click="activeTab = tab"
            >
              {{ getTabLabel(tab) }}
            </button>
          </div>

          <LayoutArticle v-if="page && page.title">
            <template #content>
              <Transition name="fade-slide" mode="out-in">
                <div :key="activeTab">
                  <template
                    v-for="group in groupedBlocks"
                    :key="group.type === 'block' ? group.item.id : group.ctas[0].id"
                  >
                    <CommonStrapiBlockRenderer v-if="group.type === 'block'" :item="group.item" />
                    <CommonCta v-else :ctas="group.ctas" class="not-prose" />
                  </template>
                </div>
              </Transition>
            </template>
          </LayoutArticle>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
  @keyframes blob {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-blob {
    animation: blob 7s infinite;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animate-fade-in {
    animation: fade-in 0.8s ease-out forwards;
    opacity: 0;
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out forwards;
    opacity: 0;
  }

  .animation-delay-200 {
    animation-delay: 0.2s;
  }

  .animation-delay-400 {
    animation-delay: 0.4s;
  }

  .animation-delay-600 {
    animation-delay: 0.6s;
  }
</style>
